import { Component, ChangeDetectionStrategy, OnInit, inject, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CalendarModule, CalendarEvent, CalendarWeekViewBeforeRenderEvent } from 'angular-calendar';
import { Subject, of, EMPTY, forkJoin } from 'rxjs';
import { expand, map, reduce, catchError } from 'rxjs/operators';
import { addDays, addMinutes, endOfDay, endOfMonth, endOfWeek, isSameDay, isSameMonth, startOfDay, startOfMonth, startOfWeek, subDays } from 'date-fns';
import { HttpClient, HttpContext } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { backendEndPointToken, authBackendEndPointToken } from '../../app.config';
import { Bundle, Schedule, Slot, Appointment } from 'fhir/r4';
import { FhirResourceService } from '../../shared/fhir-resource.service';
import { UnifiedNotificationService } from '../../shared/unified-notification.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { ErrorService } from '../../shared/error.service';
import { UtilityService } from '../../shared/utility.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuccessMessageComponent } from '../../shared/success-message/success-message.component';
import { AuthService } from '../../shared/auth/auth.service';
import { MatMenuModule } from '@angular/material/menu';
import { isHospitalAdmin, isPractitionerRole, normalizeRole } from '../../shared/system-roles';
import { FirebaseAlertService } from '../../shared/services/firebase-alert.service';
@Component({
  selector: 'app-admin-schedule-calendar',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, CalendarModule, MatMenuModule],
  templateUrl: './admin-schedule-calendar.component.html',
  styleUrls: ['./admin-schedule-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class AdminScheduleCalendarComponent implements OnInit, AfterViewInit {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLElement>;
  viewDate: Date = new Date();
  events: CalendarEvent[] = [];
  refresh = new Subject<void>();
  practitionerId: string | null = null;
  scheduleId: string | null = null;
  selectedSlots: { start: Date; end: Date }[] = [];
  existingSlots: Slot[] = [];
  private lastClickedStart: number | null = null;
  selectionMode: 'create' | 'edit' | null = null;
  private isPointerDown = false;
  private isDragging = false;
  hasSlotsInCurrentMonth: boolean = true;
  hasSlotsInCurrentWeek: boolean = true;
  reminderOptions: Array<{ intervalType: 'Week' | 'Month'; reasonType: 'Missing' | 'Incomplete'; label: string; description: string }> = [];
  /** The calendar is rendered with two segments per hour: 30 minutes each. */
  private readonly selectableSlotMinutes = 30;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(AuthService);
  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);
  private authBackendUrl = inject(authBackendEndPointToken);
  private dialog = inject(MatDialog);
  private errorService = inject(ErrorService);
  private utilityService = inject(UtilityService);
  private fhirResourceService = inject(FhirResourceService);
  private unifiedNotificationService = inject(UnifiedNotificationService);
  private firebaseAlertService = inject(FirebaseAlertService);

  ngOnInit() {
    this.practitionerId = this.route.snapshot.paramMap.get('practitionerId');
    // If caller provided a focusDate query param, initialize the calendar to that week
    const focus = this.route.snapshot.queryParamMap.get('focusDate');
    if (focus) {
      const d = new Date(focus);
      if (!isNaN(d.getTime())) {
        this.viewDate = startOfWeek(d);
      }
    }
    const user = this.auth.getCurrentUser();

    if (!user || !this.practitionerId || !this.canAccessSchedule(user, this.practitionerId)) {
      console.warn('Blocked schedule calendar access:', {
        requestedPractitionerId: this.practitionerId,
        userId: user?.fhirResourceId,
        role: user?.role
      });
      this.router.navigate(['/unauthorized']);
      return;
    }

    console.log('Schedule calendar access granted:', {
      requestedPractitionerId: this.practitionerId,
      accessorId: user.fhirResourceId,
      role: normalizeRole(user.role)
    });

    this.fetchScheduleAndSlots();
    this.checkMonthSlots();
  }

  ngAfterViewInit() {
    this.scrollTo8AM();
  }

  private scrollTo8AM() {
    // 8 AM is 8 hours from 00:00. By default, angular-calendar week view uses 30px segments, 2 per hour (60px per hour).
    // So 8 hours * 60px = 480px.
    setTimeout(() => {
      if (this.scrollContainer && this.scrollContainer.nativeElement) {
        this.scrollContainer.nativeElement.scrollTop = 480;
      }
    }, 0);
  }

  /**
   * Hospital/Super Admins may inspect any practitioner's calendar.
   * Practitioner roles may inspect only their own calendar, including Remote
   * Doctors. The route parameter is the schedule owner, not the accessor.
   */
  private canAccessSchedule(user: ReturnType<AuthService['getCurrentUser']>, requestedId: string): boolean {
    if (!user) return false;

    const role = normalizeRole(user.role);
    if (role === 'superadmin' || isHospitalAdmin(role)) return true;
    if (!isPractitionerRole(role)) return false;

    const accessorId = (user.fhirResourceId ?? user.userId ?? '').toString().trim();
    const ownerId = this.extractResourceId(requestedId);
    return !!accessorId && !!ownerId && accessorId === ownerId;
  }

  private extractResourceId(referenceOrId: string): string {
    const value = (referenceOrId ?? '').split('?')[0].trim();
    return value.split('/').filter(Boolean).pop() ?? '';
  }

  fetchScheduleAndSlots() {
    // Calculate the start and end of the currently viewed week
    const startIso = startOfWeek(this.viewDate).toISOString();
    const endIso = endOfWeek(this.viewDate).toISOString();
    
    const actor = encodeURIComponent(`Practitioner/${this.practitionerId}`);
    const startGe = encodeURIComponent(startIso);
    const startLe = encodeURIComponent(endIso);
    
    // Fetch all slots for this practitioner within the current week across ALL their schedules
    const queries = `schedule.actor=${actor}&start=ge${startGe}&start=le${startLe}`;
    
    this.utilityService.fetchAll<Slot>({
      resourceType: 'Slot',
      queries: queries
    }).subscribe({
      next: (slots) => {
        this.existingSlots = slots.filter(r => r.status !== 'entered-in-error') || [];
        this.hasSlotsInCurrentWeek = this.existingSlots.length > 0;
        this.rebuildReminderOptions();
        this.refresh.next();
      },
      error: (err) => {
        console.error('Error fetching slots for the week:', err);
        this.hasSlotsInCurrentWeek = false;
        this.rebuildReminderOptions();
      }
    });
  }

  // Handle when the user navigates to a different week
  onViewDateChange() {
    this.fetchScheduleAndSlots();
    this.checkMonthSlots();
  }

  beforeWeekViewRender(renderEvent: CalendarWeekViewBeforeRenderEvent) {
    renderEvent.hourColumns.forEach((hourColumn: any) => {
      hourColumn.hours.forEach((hour: any) => {
        hour.segments.forEach((segment: any) => {
          const segmentStart = segment.date.getTime();
          
          // Check if this segment overlaps with any existing slot. This works
          // for slots of any duration, including an existing 08:00-14:00 slot.
          const hasExisting = this.existingSlots.some(slot => {
            const start = new Date(slot.start!).getTime();
            const end = new Date(slot.end!).getTime();
            const segmentEnd = segmentStart + this.selectableSlotMinutes * 60 * 1000;
            return segmentStart < end && start < segmentEnd;
          });

          // Check if this segment is manually selected
          const isSelected = this.selectedSlots.some(s => s.start.getTime() === segmentStart);

          // Apply combined classes so create vs edit selection is visually distinct.
          if (isSelected && hasExisting) {
            segment.cssClass = 'cal-segment-selected-edit';
          } else if (isSelected && !hasExisting) {
            segment.cssClass = 'cal-segment-selected-create';
          } else if (hasExisting) {
            segment.cssClass = 'cal-segment-existing-slot';
          } else {
            segment.cssClass = '';
          }
        });
      });
    });
  }

  hourSegmentClicked(event: MouseEvent, date: Date) {
    // Deprecated: old click handler. Use pointer handlers for selection and drag.
    return;
  }

  onPointerDown(event: PointerEvent, date: Date) {
    event.preventDefault();
    const start = date;
    const segmentStart = start.getTime();
    const segmentEnd = segmentStart + this.selectableSlotMinutes * 60 * 1000;

    const overlapsExisting = this.existingSlots.some(slot => {
      const slotStart = new Date(slot.start!).getTime();
      const slotEnd = new Date(slot.end!).getTime();
      return segmentStart < slotEnd && slotStart < segmentEnd;
    });

    this.isPointerDown = true;
    this.isDragging = false;
    this.selectionMode = overlapsExisting ? 'edit' : 'create';
    this.lastClickedStart = segmentStart;

    const isCtrl = (event as PointerEvent).ctrlKey || (event as PointerEvent).metaKey;
    const isShift = (event as PointerEvent).shiftKey;

    // Ctrl-click: toggle single segment (existing behavior)
    if (isCtrl) {
      const idx = this.selectedSlots.findIndex(s => s.start.getTime() === segmentStart);
      if (idx > -1) {
        this.selectedSlots.splice(idx, 1);
      } else {
        // Only add if mode matches
        if ((this.selectionMode === 'create' && !overlapsExisting) || (this.selectionMode === 'edit' && overlapsExisting)) {
          this.selectedSlots.push({ start, end: new Date(segmentEnd) });
        }
      }
      this.refresh.next();
      return;
    }

    // Shift-click: select a contiguous range between lastClickedStart and this segment
    if (isShift && this.lastClickedStart !== null) {
      const low = Math.min(this.lastClickedStart, segmentStart);
      const high = Math.max(this.lastClickedStart, segmentStart);
      const stepMs = this.selectableSlotMinutes * 60 * 1000;
      for (let t = low; t <= high; t += stepMs) {
        const segStart = t;
        const segEnd = segStart + stepMs;
        const overlaps = this.existingSlots.some(slot => {
          const slotStart = new Date(slot.start!).getTime();
          const slotEnd = new Date(slot.end!).getTime();
          return segStart < slotEnd && slotStart < segEnd;
        });
        if ((this.selectionMode === 'create' && !overlaps) || (this.selectionMode === 'edit' && overlaps)) {
          const already = this.selectedSlots.some(s => s.start.getTime() === segStart);
          if (!already) this.selectedSlots.push({ start: new Date(segStart), end: new Date(segEnd) });
        }
      }
      this.lastClickedStart = segmentStart;
      this.refresh.next();
      return;
    }

    // Plain click: toggle (remove if already selected, otherwise add)
    if ((this.selectionMode === 'create' && !overlapsExisting) || (this.selectionMode === 'edit' && overlapsExisting)) {
      const idx = this.selectedSlots.findIndex(s => s.start.getTime() === segmentStart);
      if (idx > -1) {
        this.selectedSlots.splice(idx, 1);
      } else {
        this.selectedSlots.push({ start, end: new Date(segmentEnd) });
      }
    }
    this.lastClickedStart = segmentStart;
    this.refresh.next();
  }

  onPointerEnter(event: PointerEvent, date: Date) {
    if (!this.isPointerDown) return;
    const start = date;
    const segmentStart = start.getTime();
    const segmentEnd = segmentStart + this.selectableSlotMinutes * 60 * 1000;

    if (segmentStart === this.lastClickedStart) return;
    this.isDragging = true;

    const overlapsExisting = this.existingSlots.some(slot => {
      const slotStart = new Date(slot.start!).getTime();
      const slotEnd = new Date(slot.end!).getTime();
      return segmentStart < slotEnd && slotStart < segmentEnd;
    });

    // Only add segments that match selectionMode
    if ((this.selectionMode === 'create' && overlapsExisting) || (this.selectionMode === 'edit' && !overlapsExisting)) {
      return;
    }

    const exists = this.selectedSlots.some(s => s.start.getTime() === segmentStart);
    if (!exists) this.selectedSlots.push({ start, end: new Date(segmentEnd) });
    this.lastClickedStart = segmentStart;
    this.refresh.next();
  }

  @HostListener('document:pointerup', ['$event'])
  onDocumentPointerUp(event: PointerEvent) {
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.isDragging = false;
    if (!this.selectedSlots.length) this.selectionMode = null;
    this.refresh.next();
  }

  checkMonthSlots() {
    if (!this.practitionerId) return;

    const startOfMonthIso = startOfMonth(this.viewDate).toISOString();
    const endOfMonthIso = endOfMonth(this.viewDate).toISOString();

    const actor = encodeURIComponent(`Practitioner/${this.practitionerId}`);
    const startGe = encodeURIComponent(startOfMonthIso);
    const startLe = encodeURIComponent(endOfMonthIso);

    const queries = `schedule.actor=${actor}&start=ge${startGe}&start=le${startLe}&_summary=count`;

    this.http.get<any>(`${this.backendUrl}/Slot?${queries}`).subscribe({
      next: (bundle) => {
        const total = Number(bundle?.total ?? 0);
        const entryCount = Array.isArray(bundle?.entry) ? bundle.entry.length : 0;
        this.hasSlotsInCurrentMonth = total > 0 || entryCount > 0;
        this.rebuildReminderOptions();
        this.refresh.next();
      },
      error: (err) => {
        console.error('Error checking month slots:', err);
        this.hasSlotsInCurrentMonth = true;
        this.rebuildReminderOptions();
        this.refresh.next();
      }
    });
  }

  private rebuildReminderOptions(): void {
    this.reminderOptions = [];

    this.reminderOptions.push({
      intervalType: 'Week',
      reasonType: 'Incomplete',
      label: 'Remind for incomplete week',
      description: 'Schedule coverage is present, but the week may still need review.'
    });

    this.reminderOptions.push({
      intervalType: 'Month',
      reasonType: 'Incomplete',
      label: 'Remind for incomplete month',
      description: 'Schedule coverage may be incomplete for the current month.'
    });

    if (!this.hasSlotsInCurrentWeek) {
      this.reminderOptions.push({
        intervalType: 'Week',
        reasonType: 'Missing',
        label: 'Remind for missing week',
        description: 'No slots were found for the current week.'
      });
    }

    if (!this.hasSlotsInCurrentMonth) {
      this.reminderOptions.push({
        intervalType: 'Month',
        reasonType: 'Missing',
        label: 'Remind for missing month',
        description: 'No slots were found for the current month.'
      });
    }
  }

  private buildReminderPayload(intervalType: 'Week' | 'Month', reasonType: 'Missing' | 'Incomplete'): any {
    const rangeStart = intervalType === 'Week' ? startOfWeek(this.viewDate) : startOfMonth(this.viewDate);
    const rangeEnd = intervalType === 'Week' ? endOfWeek(this.viewDate) : endOfMonth(this.viewDate);

    return {
      targetUserId: this.practitionerId,
      intervalType,
      startDate: rangeStart.toISOString(),
      endDate: rangeEnd.toISOString(),
      reasonType
    };
  }

  sendReminder(intervalType: 'Week' | 'Month', reasonType: 'Missing' | 'Incomplete') {
    if (!this.practitionerId) return;

    const message = reasonType === 'Missing'
      ? `Send a reminder for a missing ${intervalType.toLowerCase()} schedule?`
      : `Send a reminder for an incomplete ${intervalType.toLowerCase()} schedule?`;

    const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Send reminder',
        message,
        confirmText: 'Send reminder',
        cancelText: 'Cancel'
      }
    });

    confirmRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.http.post(`${this.authBackendUrl}/api/schedules/remind-practitioner`, this.buildReminderPayload(intervalType, reasonType)).subscribe({
        next: () => {
          const alert = {
            title: 'Schedule reminder',
            description: `Reminder sent for ${intervalType.toLowerCase()} ${reasonType.toLowerCase()} schedule.`,
            url: '/app/schedules',
            query: {
              practitionerId: this.practitionerId,
              intervalType,
              reasonType
            }
          };

          const orgId = this.auth.getOrganizationId?.() || '';
          if (this.practitionerId && orgId) {
            this.firebaseAlertService.postUserAlert(orgId, this.practitionerId, alert).subscribe();
          }

          this.sn.openFromComponent(SuccessMessageComponent, {
            data: { message: `Reminder sent successfully for ${intervalType.toLowerCase()} ${reasonType.toLowerCase()} schedule.` },
            duration: 3000
          });
        },
        error: (err) => {
          console.error('Error sending reminder:', err);
          const msg = err.error?.message || 'Failed to send reminder.';
          this.errorService.openandCloseError(msg);
        }
      });
    });
  }

  assignOverrides() {
    if (this.selectedSlots.length === 0 || !this.practitionerId) return;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Assign Overrides',
        message: `Are you sure you want to assign ${this.selectedSlots.length} new slots to this schedule?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // 1. Fetch Schedule for the specific dates
        // Use the start of the first selected slot and end of the last selected slot
        const sortedSlots = [...this.selectedSlots].sort((a, b) => a.start.getTime() - b.start.getTime());
        const earliestStart = sortedSlots[0].start.toISOString();
        const latestEnd = sortedSlots[sortedSlots.length - 1].end.toISOString();

        const actor = encodeURIComponent(`Practitioner/${this.practitionerId}`);
        const dateGe = encodeURIComponent(earliestStart);
        const dateLe = encodeURIComponent(latestEnd);

        this.utilityService.fetchAll<Schedule>({
          resourceType: 'Schedule',
          queries: `actor=${actor}&date=ge${dateGe}&date=le${dateLe}`
        }).subscribe({
          next: (schedules) => {
            if (schedules && schedules.length > 0) {
              this.createSlotsWithSchedule(schedules[0].id!);
            } else {
              this.createNewScheduleAndSlots(sortedSlots[0].start);
            }
          },
          error: (err) => {
            console.error('Error fetching schedules:', err);
            this.errorService.openandCloseError('Failed to fetch practitioner schedules.');
          }
        });
      }
    });
  }

  private createNewScheduleAndSlots(dateInMonth: Date) {
    const newSchedule: Schedule = {
      resourceType: 'Schedule',
      actor: [{ reference: `Practitioner/${this.practitionerId}` }],
      planningHorizon: {
        start: startOfMonth(dateInMonth).toISOString(),
        end: endOfMonth(dateInMonth).toISOString()
      }
    };

    this.http.post<Schedule>(`${this.backendUrl}/Schedule`, newSchedule).subscribe({
      next: (createdSchedule) => {
        this.createSlotsWithSchedule(createdSchedule.id!);
      },
      error: (err) => {
        console.error('Error creating schedule:', err);
        this.errorService.openandCloseError('Failed to create new schedule.');
      }
    });
  }

  private createSlotsWithSchedule(scheduleId: string) {
    const newSlots: Slot[] = this.selectedSlots.map(s => ({
      resourceType: 'Slot',
      schedule: { reference: `Schedule/${scheduleId}` },
      status: 'free',
      start: s.start.toISOString(),
      end: s.end.toISOString()
    }));

    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: newSlots.map(slot => ({
        resource: slot,
        request: { method: 'POST', url: 'Slot' }
      }))
    };
    

    this.http.post(this.backendUrl, bundle).subscribe({
      next: () => {
        this.selectedSlots = [];
        this.fetchScheduleAndSlots();
        this.checkMonthSlots();
        // this..openandCloseError('Overrides assigned!');
        this.sn.openFromComponent(SuccessMessageComponent, {
                    data: { message: 'Overrides assigned' },
                    duration: 3000
                  });
      },
      error: (err) => {
        console.error('Error assigning slots:', err);
        this.errorService.openandCloseError('Failed to assign slots.');
      }
    });
  }
  sn = inject(MatSnackBar)

  removeSelected() {
    if (this.selectedSlots.length === 0) return;

    // If any selected segment overlaps an existing persisted slot, block removal
    const overlapsExisting = this.selectedSlots.some(sel => {
      const s = sel.start.getTime();
      const e = sel.end.getTime();
      return this.existingSlots.some(slot => {
        const slotStart = new Date(slot.start!).getTime();
        const slotEnd = new Date(slot.end!).getTime();
        return s < slotEnd && slotStart < e;
      });
    });

    if (overlapsExisting) {
      // Check if selection exactly matches one or more existing persisted slot boundaries
      const matchedSlots = this.selectedSlots.map(sel => {
        return this.existingSlots.find(slot => {
          const slotStart = new Date(slot.start!).getTime();
          const slotEnd = new Date(slot.end!).getTime();
          return slotStart === sel.start.getTime() && slotEnd === sel.end.getTime();
        });
      }).filter(Boolean) as Slot[];

      if (matchedSlots.length === this.selectedSlots.length && matchedSlots.every(s => !!s.id)) {
        // Before retiring exact-match persisted slots, check for overlapping appointments and block if any exist
        const slotIds = matchedSlots.map(s => s.id!).filter(Boolean) as string[];
        const practitionerRef = this.practitionerId?.startsWith('Practitioner/') ? this.practitionerId! : `Practitioner/${this.practitionerId}`;

        const slotAppointments$ = slotIds.length ? this.fetchAppointmentsForSlots(slotIds) : of([] as Appointment[]);
        const coverageAppointments$ = this.fetchAppointmentsForPractitioner(practitionerRef);

        forkJoin({ bySlot: slotAppointments$, byCoverage: coverageAppointments$ }).subscribe({
          next: ({ bySlot, byCoverage }) => {
            const byId = new Map<string, Appointment>();
            [...bySlot, ...byCoverage].forEach(a => { if (a.id) byId.set(a.id, a); });
            const allAppointments = Array.from(byId.values());

            // Find any appointment overlapping any of the exact slots
            const overlapping = allAppointments.filter(a => matchedSlots.some(slot => this.appointmentOverlapsInterval(a, new Date(slot.start!).getTime(), new Date(slot.end!).getTime())));

            if (overlapping.length) {
              // Block exact-retire when appointments overlap — show same warning as partial-overlap path
              this.dialog.open(ConfirmationDialogComponent, {
                data: {
                  title: 'Cannot remove selection',
                  message: `${overlapping.length} active appointment(s) overlap the selected slots. Remove or reschedule these appointments first.`
                }
              });
              return;
            }

            // No overlapping appointments: proceed with retirement confirmation
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
              data: {
                title: 'Remove selected slots',
                message: `This will retire ${matchedSlots.length} existing slot(s). Are you sure?`
              }
            });

            dialogRef.afterClosed().subscribe(confirmed => {
              if (!confirmed) return;

              // For matched exact slots we can retire directly
              const bundle: Bundle = {
                resourceType: 'Bundle',
                type: 'transaction',
                entry: matchedSlots.map(slot => ({
                  resource: { ...(slot as any), resourceType: 'Slot', status: 'entered-in-error' } as Slot,
                  request: { method: 'PUT', url: `Slot/${slot.id}` }
                }))
              };

              this.fhirResourceService.postBundle(bundle).subscribe({
                next: () => {
                  this.selectedSlots = [];
                  this.fetchScheduleAndSlots();
                  this.checkMonthSlots();
                  this.sn.openFromComponent(SuccessMessageComponent, { data: { message: 'Slots removed' }, duration: 3000 });
                },
                error: (err) => {
                  console.error('Failed to retire slots:', err);
                  this.errorService.openandCloseError('Failed to remove slots.');
                }
              });
            });
          },
          error: (err) => { console.error('Failed fetching appointments for validation:', err); this.errorService.openandCloseError('Failed to validate appointments before slot removal.'); }
        });

        return;
      }

      // There are partial overlaps: attempt safe split with appointment checks
      const practitionerRef = this.practitionerId?.startsWith('Practitioner/') ? this.practitionerId! : `Practitioner/${this.practitionerId}`;

      // Determine affected slots and deletion intervals per slot
      const affected: { slot: Slot; deletions: { from: number; to: number }[] }[] = [];
      for (const slot of this.existingSlots) {
        const slotStart = new Date(slot.start!).getTime();
        const slotEnd = new Date(slot.end!).getTime();
        const deletions: { from: number; to: number }[] = [];
        for (const sel of this.selectedSlots) {
          const s = sel.start.getTime();
          const e = sel.end.getTime();
          const from = Math.max(slotStart, s);
          const to = Math.min(slotEnd, e);
          if (from < to) deletions.push({ from, to });
        }
        if (deletions.length) affected.push({ slot, deletions });
      }

      if (!affected.length) {
        this.dialog.open(ConfirmationDialogComponent, {
          data: { title: 'Cannot remove selection', message: 'No matching persisted slots found for the selection.' }
        });
        return;
      }

      // Build a coverage window to fetch practitioner appointments once
      const coverageStart = Math.min(...affected.map(a => Math.min(...a.deletions.map(d => d.from))));
      const coverageEnd = Math.max(...affected.map(a => Math.max(...a.deletions.map(d => d.to))));

      this.fetchAppointmentsForPractitioner(practitionerRef).subscribe({
        next: (appointments) => {
          // Filter active appointments overlapping any deletion interval
          const blocking = [] as Appointment[];
          for (const a of appointments) {
            if (this.appointmentIsLocked(a)) {
              // locked appointments always block
              for (const aff of affected) {
                for (const d of aff.deletions) {
                  if (this.appointmentOverlapsInterval(a, d.from, d.to)) {
                    blocking.push(a);
                  }
                }
              }
            } else {
              // non-locked appointments still block if they overlap
              for (const aff of affected) {
                for (const d of aff.deletions) {
                  if (this.appointmentOverlapsInterval(a, d.from, d.to)) {
                    blocking.push(a);
                  }
                }
              }
            }
          }

          if (blocking.length) {
            this.dialog.open(ConfirmationDialogComponent, {
              data: {
                title: 'Cannot remove selection',
                message: `${blocking.length} active appointment(s) overlap the selected removal intervals. Remove or reschedule these appointments first.`
              }
            });
            return;
          }

          // Safe to split/retire: create replacement slots for preserved intervals
          const entries: NonNullable<Bundle['entry']> = [];

          for (const aff of affected) {
            const slot = aff.slot;
            const slotStart = new Date(slot.start!).getTime();
            const slotEnd = new Date(slot.end!).getTime();

            // Compute preserved slices by subtracting deletions from [slotStart, slotEnd)
            let cursor = slotStart;
            const sortedDeletions = aff.deletions.sort((a, b) => a.from - b.from);
            for (const del of sortedDeletions) {
              if (cursor < del.from) {
                // preserved interval [cursor, del.from)
                entries.push({
                  resource: {
                    resourceType: 'Slot',
                    schedule: slot.schedule,
                    status: slot.status ?? 'free',
                    start: new Date(cursor).toISOString(),
                    end: new Date(del.from).toISOString()
                  } as Slot,
                  request: { method: 'POST', url: 'Slot' }
                });
              }
              cursor = Math.max(cursor, del.to);
            }
            if (cursor < slotEnd) {
              entries.push({
                resource: {
                  resourceType: 'Slot',
                  schedule: slot.schedule,
                  status: slot.status ?? 'free',
                  start: new Date(cursor).toISOString(),
                  end: new Date(slotEnd).toISOString()
                } as Slot,
                request: { method: 'POST', url: 'Slot' }
              });
            }

            // Retire original slot
            if (slot.id) {
              entries.push({
                resource: { ...(slot as any), resourceType: 'Slot', status: 'entered-in-error' } as Slot,
                request: { method: 'PUT', url: `Slot/${slot.id}` }
              });
            }
          }

          if (entries.length === 0) {
            this.errorService.openandCloseError('Nothing to update.');
            return;
          }

          const tx: Bundle = { resourceType: 'Bundle', type: 'transaction', entry: entries };
          this.fhirResourceService.postBundle(tx).subscribe({
            next: () => {
              this.selectedSlots = [];
              this.fetchScheduleAndSlots();
              this.checkMonthSlots();
              this.sn.openFromComponent(SuccessMessageComponent, { data: { message: 'Slots updated' }, duration: 3000 });
            },
            error: (err) => {
              console.error('Failed to update slots:', err);
              this.errorService.openandCloseError('Failed to update slots.');
            }
          });
        },
        error: (err) => {
          console.error('Failed to fetch appointments for validation:', err);
          this.errorService.openandCloseError('Failed to validate appointments before slot removal.');
        }
      });
      return;
    }

    // Safe: selection only contains non-persisted segments; clear them locally
    this.selectedSlots = [];
    this.refresh.next();
  }

  async forceRemoveSelected() {
    if (this.selectedSlots.length === 0) return;

    // Determine affected persisted slots and deletion intervals
    const affected: { slot: Slot; deletions: { from: number; to: number }[] }[] = [];
    for (const slot of this.existingSlots) {
      const slotStart = new Date(slot.start!).getTime();
      const slotEnd = new Date(slot.end!).getTime();
      const deletions: { from: number; to: number }[] = [];
      for (const sel of this.selectedSlots) {
        const s = sel.start.getTime();
        const e = sel.end.getTime();
        const from = Math.max(slotStart, s);
        const to = Math.min(slotEnd, e);
        if (from < to) deletions.push({ from, to });
      }
      if (deletions.length) affected.push({ slot, deletions });
    }

    if (!affected.length) {
      this.dialog.open(ConfirmationDialogComponent, { data: { title: 'No persisted slots', message: 'No persisted slots are affected by the selection.' } });
      return;
    }

    // Collect slot IDs
    const slotIds = Array.from(new Set(affected.map(a => a.slot.id).filter(Boolean) as string[]));
    const practitionerRef = this.practitionerId?.startsWith('Practitioner/') ? this.practitionerId! : `Practitioner/${this.practitionerId}`;

    // Fetch appointments related to slots and practitioner coverage
    const slotAppointments$ = slotIds.length ? this.fetchAppointmentsForSlots(slotIds) : of([] as Appointment[]);
    const coverageAppointments$ = this.fetchAppointmentsForPractitioner(practitionerRef);

    forkJoin({ bySlot: slotAppointments$, byCoverage: coverageAppointments$ }).subscribe({
      next: ({ bySlot, byCoverage }) => {
        const byId = new Map<string, Appointment>();
        [...bySlot, ...byCoverage].forEach(a => { if (a.id) byId.set(a.id, a); });
        const allAppointments = Array.from(byId.values());

        // Find appointments overlapping any deletion interval
        const overlapping = allAppointments.filter(a => affected.some(aff => aff.deletions.some(d => this.appointmentOverlapsInterval(a, d.from, d.to))));

        const cancellable = overlapping.filter(a => !this.appointmentIsLocked(a));

        const messages: string[] = [];
        messages.push(`This action will modify ${affected.length} persisted slot(s).`);
        messages.push(`${overlapping.length} appointment(s) overlap the selected intervals; ${cancellable.length} are cancellable.`);
        overlapping.slice(0, 10).forEach(a => messages.push(this.formatAppointmentSummary(a)));

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { title: 'Force remove and cancel appointments?', messagesArray: messages, confirmText: 'Force remove', cancelText: 'Keep' } });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
          if (!confirmed) return;

          const timestamp = new Date().toISOString();
          const entries: NonNullable<Bundle['entry']> = [];

          // For each affected slot: create preserved slices and retire original
          for (const aff of affected) {
            const slot = aff.slot;
            const slotStart = new Date(slot.start!).getTime();
            const slotEnd = new Date(slot.end!).getTime();
            let cursor = slotStart;
            const sorted = aff.deletions.sort((a, b) => a.from - b.from);
            for (const del of sorted) {
              if (cursor < del.from) {
                entries.push({ resource: { resourceType: 'Slot', schedule: slot.schedule, status: slot.status ?? 'free', start: new Date(cursor).toISOString(), end: new Date(del.from).toISOString() } as Slot, request: { method: 'POST', url: 'Slot' } });
              }
              cursor = Math.max(cursor, del.to);
            }
            if (cursor < slotEnd) {
              entries.push({ resource: { resourceType: 'Slot', schedule: slot.schedule, status: slot.status ?? 'free', start: new Date(cursor).toISOString(), end: new Date(slotEnd).toISOString() } as Slot, request: { method: 'POST', url: 'Slot' } });
            }
            if (slot.id) entries.push({ resource: { ...(slot as any), resourceType: 'Slot', status: 'entered-in-error' } as Slot, request: { method: 'PUT', url: `Slot/${slot.id}` } });
          }

          // Cancel cancellable appointments and add comms
          cancellable.forEach(appointment => {
            if (!appointment.id) return;
            const updated: Appointment = { ...(appointment as any), resourceType: 'Appointment', id: appointment.id, status: 'cancelled', comment: this.appendAppointmentComment(appointment, undefined, timestamp) } as Appointment;
            entries.push({ resource: updated, request: { method: 'PUT', url: `Appointment/${appointment.id}` } });

            const recipients = (appointment.participant ?? []).map(p => p.actor?.reference).filter(Boolean) as string[];
            if (recipients.length) {
              const comm: any = { resourceType: 'CommunicationRequest', status: 'active', authoredOn: timestamp, recipient: recipients.map(r => ({ reference: r })), payload: [{ contentString: `Appointment ${this.formatAppointmentSummary(appointment)} was cancelled due to schedule change.` }], about: appointment.id ? [{ reference: `Appointment/${appointment.id}` }] : undefined };
              const patientRef = this.extractPatientReferenceFromAppointment(appointment) ?? recipients[0];
              if (patientRef) comm.subject = { reference: patientRef };
              entries.push({ resource: comm as any, request: { method: 'POST', url: 'CommunicationRequest' } });
            }
          });

          if (entries.length === 0) {
            this.errorService.openandCloseError('Nothing to update.');
            return;
          }

          const tx: Bundle = { resourceType: 'Bundle', type: 'transaction', entry: entries };
          this.fhirResourceService.postBundle(tx).subscribe({
            next: (res: any) => {
              try {
                // Notify via unified channels using the cancellable appointments list
                this.unifiedNotificationService.notifyAppointmentCancellations(cancellable, res?.bundle);
              } catch (e) {
                console.warn('Unified notification failed', e);
              }
              this.selectedSlots = [];
              this.fetchScheduleAndSlots();
              this.checkMonthSlots();
              this.sn.openFromComponent(SuccessMessageComponent, { data: { message: 'Slots and appointments updated' }, duration: 3000 });
            },
            error: (err) => { console.error('Force remove failed', err); this.errorService.openandCloseError('Force remove failed.'); }
          });
        });
      },
      error: (err) => { console.error('Failed fetching appointments', err); this.errorService.openandCloseError('Failed to fetch appointments for preview.'); }
    });
  }

  // --- Partial removal helpers ---
  private fetchAllBundleResources<T>(initialUrl: string, maxPages = 100, context?: HttpContext) {
    return this.http.get<Bundle<T>>(initialUrl, context ? { context } : {}).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= maxPages) return EMPTY;

        const nextLink = (bundle?.link ?? []).find(
          (link) => String(link?.relation ?? '').toLowerCase() === 'next'
        )?.url;
        const nextUrl = nextLink ? this.normalizeFhirNextLink(nextLink) : null;

        return nextUrl ? this.http.get<Bundle<T>>(nextUrl, context ? { context } : {}) : EMPTY;
      }),
      map((bundle) => (bundle?.entry ?? []).map((entry) => entry.resource as T).filter(Boolean)),
      reduce((resources, pageResources) => resources.concat(pageResources), [] as T[])
    );
  }

  private normalizeFhirNextLink(nextLink: string | undefined): string | null {
    const value = String(nextLink ?? '').trim();
    if (!value) return null;

    try {
      const nextUrl = new URL(value, `${this.backendUrl.replace(/\/$/, '')}/`);
      const slotPathIndex = nextUrl.pathname.lastIndexOf('/Slot');
      if (slotPathIndex < 0) return value;

      const backendBase = this.backendUrl.replace(/\/$/, '');
      return `${backendBase}${nextUrl.pathname.slice(slotPathIndex)}${nextUrl.search}${nextUrl.hash}`;
    } catch {
      return value;
    }
  }

  private fetchAppointmentsForPractitioner(practitionerReference: string) {
    const url = `${this.backendUrl}/Appointment?practitioner=${encodeURIComponent(practitionerReference)}&_count=200`;
    return this.fetchAllBundleResources<Appointment>(url, 100).pipe(
      map((resources) => resources.filter((resource): resource is Appointment => resource?.resourceType === 'Appointment')),
      catchError(() => of([] as Appointment[]))
    );
  }

  private fetchAppointmentsForSlots(slotIds: string[]) {
    if (!slotIds.length) return of([] as Appointment[]);
    const chunkSize = 10;
    const requests: Array<any> = [];
    for (let i = 0; i < slotIds.length; i += chunkSize) {
      const chunk = slotIds.slice(i, i + chunkSize);
      const ids = chunk.map(id => `Slot/${encodeURIComponent(id)}`).join(',');
      const url = `${this.backendUrl}/Appointment?slot=${ids}&_count=200`;
      requests.push(this.fetchAllBundleResources<Appointment>(url, 100).pipe(map((resources) => resources.filter((r): r is Appointment => r?.resourceType === 'Appointment')), catchError(() => of([] as Appointment[]))));
    }
    return forkJoin(requests).pipe(map((parts: Appointment[][]) => parts.flat()));
  }

  private appointmentOverlapsInterval(appointment: Appointment, from: number, to: number): boolean {
    const start = appointment.start ? new Date(appointment.start).getTime() : NaN;
    const end = appointment.end ? new Date(appointment.end).getTime() : NaN;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return false;
    return start < to && from < end;
  }

  private appointmentIsLocked(appointment: Appointment): boolean {
    const lockedStatuses = new Set(['fulfilled', 'completed', 'cancelled', 'noshow', 'entered-in-error']);
    const status = (appointment.status ?? '').toLowerCase();
    return lockedStatuses.has(status);
  }

  private formatAppointmentSummary(appointment: Appointment): string {
    const date = appointment.start ? new Date(appointment.start) : null;
    const formatted = date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : 'unscheduled time';
    return appointment.id ? `${appointment.id} (${formatted})` : formatted;
  }

  private appendAppointmentComment(appointment: Appointment, scheduleId: string | undefined, timestamp: string): string {
    const base = (appointment.comment ?? '').trim();
    const note = `Cancelled due to schedule change at ${timestamp}.`;
    return base ? `${base}\n${note}` : note;
  }

  private extractPatientReferenceFromAppointment(appointment: Appointment): string | null {
    for (const participant of appointment.participant ?? []) {
      const ref = participant.actor?.reference ?? '';
      if (ref.startsWith('Patient/')) return ref;
    }
    return null;
  }
}
